import { PAGE_TYPE } from '../../constants';
import { buildPagePerStoreBreadcrumbs } from '../../structured-data/build-pps-breadcrumbs';
import { buildStoreDetailStructuredData } from '../../structured-data/build-store-detail-structured-data';

/**
 * Generates the title for the Store Reviews page.
 *
 * @param {Object} params - Parameters for generating the title.
 * @param {string} params.city - The city where the store is located.
 * @param {string} [params.storeName] - The name of the store.
 * @param {string} params.postalCode - The postal code of the store.
 * @param {number|string} params.seoPageNumber - The page number for SEO pagination.
 * @param {string} params.state - The state where the store is located.
 * @returns {string} The generated title for the Store Reviews page.
 */
export const generateStoreReviewsTitle = ({
  city,
  storeName = '',
  postalCode,
  seoPageNumber,
  state
}) => {
  if (city && state && postalCode) {
    return `${storeName} Home Depot Store Reviews, Pg ${seoPageNumber} | ${city}, ${state} ${postalCode}`.trim();
  }
  return `${storeName} Home Depot Store Reviews, Pg ${seoPageNumber}`.trim();
};

/**
 * Generates the meta description for the Store Reviews page.
 *
 * @param {Object} params - Parameters for generating the description.
 * @param {string} params.city - The city where the store is located.
 * @param {string} params.state - The state where the store is located.
 * @param {string} params.storeName - The name of the store.
 * @returns {string} The generated meta description for the Store Reviews page.
 */
export const generateStoreReviewsDesc = ({ city, state, storeName }) => {
  if (!city || !state || !storeName) {
    return 'The Home Depot';
  }

  const descriptionPostfix = `${storeName} store in ${city}, ${state}.`;
  return `See what shoppers are saying about their experience visiting The Home Depot ${descriptionPostfix}`;
};

/**
 * Generates an array of schema objects for use in generating Store Reviews structured data script tags.
 * If `emtSchema` is provided, it will be used as the only schema in the array.
 * Otherwise, default schemas for breadcrumbs and local business are generated using the provided store and review info.
 *
 * @param {Object} params - Parameters for generating schemas.
 * @param {string} [params.emtSchema] - A string containing the JSON-LD schema content for Store Reviews.
 * @param {Object} params.storeInfo - The store information object.
 * @param {Object} params.reviewInfo - The review information object.
 * @returns {Array} An array of schema objects, each with a `content` (string) and `id` (string).
 */
export const getStoreReviewsSchemas = ({ emtSchema, storeInfo, reviewInfo }) => {
  if (emtSchema) {
    return [{ content: emtSchema, id: 'storeReviewsStructuredData' }];
  }
  return ([
    {
      content: buildPagePerStoreBreadcrumbs({
        storeInfo,
        departmentName: 'Reviews',
        departmentID: `${PAGE_TYPE.REVIEWS}/1`
      }),
      id: 'storeReviewsStructuredBreadcrumb'
    },
    {
      content: buildStoreDetailStructuredData({ reviewInfo, storeInfo }),
      id: 'storeReviewsStructuredLocalBusinessData'
    }
  ]);
};